import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import DynamicIcon from '../components/DynamicIcon';
import { motion, AnimatePresence } from 'framer-motion';
import ruzyAiData from '../default_datas/ruzyAiData';

const RuzyAI = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [activeFeature, setActiveFeature] = useState(0);

  const t = (obj, field) => {
    if (!obj) return '';
    return lang === 'en' ? (obj[`${field}_en`] || obj[field] || '') : (obj[field] || '');
  };

  const { hero, stats, features, howItWorks, benefits } = ruzyAiData;

  return (
    <>
      <Seo
        title="RUZY AI | Yapay Zeka Destekli BGYS"
        description="RUZY AI ile ISO 27001 süreçlerinizi otomatize edin. Akıllı kontrol önerileri, risk analizi, otomatik politika oluşturma ve daha fazlası."
        path="/ruzy-ai"
      />

      <div className="min-h-screen bg-[#f5faf9] font-poppins text-[#0D3A2F] overflow-x-hidden">

        {/* ── 1. HERO ── */}
        <ScrollReveal>
          <section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0D3A2F] via-[#1a4a3f] to-[#0D3A2F]" />
            <div className="absolute inset-0">
              <div className="absolute top-0 left-0 w-full h-full opacity-30">
                <div className="absolute top-10 left-10 w-20 h-20 lg:w-32 lg:h-32 border border-[#2a9d8f]/30 rounded-full animate-pulse" />
                <div className="absolute top-1/2 right-1/3 w-12 h-12 lg:w-16 lg:h-16 bg-[#2a9d8f]/10 rounded-full animate-ping" />
              </div>
            </div>

            <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-32 text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2a9d8f]/20 to-[#2a9d8f]/10 backdrop-blur-sm text-[#2a9d8f] text-[10px] lg:text-sm font-bold px-4 lg:px-5 py-2 lg:2.5 rounded-full mb-6 lg:mb-8 border border-[#2a9d8f]/30">
                <span className="relative flex h-2 w-2 lg:h-3 lg:w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2a9d8f] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 lg:h-3 lg:w-3 bg-[#2a9d8f]"></span>
                </span>
                {t(hero, 'badge')}
              </div>

              <h1 className="text-3xl lg:text-7xl font-bold text-white mb-4 lg:mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-[#2a9d8f] to-white bg-clip-text text-transparent">
                  {hero.title}
                </span>
              </h1>

              <p className="text-base lg:text-2xl text-[#2a9d8f] font-medium mb-4">
                {t(hero, 'subtitle')}
              </p>

              <p className="text-sm lg:text-lg text-white/70 mb-8 lg:mb-10 max-w-3xl mx-auto leading-relaxed">
                {t(hero, 'description')}
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-3 lg:gap-4">
                <button
                  onClick={() => navigate('/iletisim')}
                  className="bg-[#2a9d8f] text-white px-8 py-3.5 lg:py-4 rounded-xl font-bold text-sm lg:text-base flex items-center justify-center gap-2"
                >
                  <DynamicIcon name="MdAutoAwesome" size={18} />
                  {lang === 'en' ? 'Try RUZY AI' : 'RUZY AI\'ı Dene'}
                </button>
                <button
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-8 py-3.5 rounded-xl font-bold text-sm lg:text-base flex items-center justify-center gap-2"
                >
                  {lang === 'en' ? 'Explore Features' : 'Özellikleri Keşfet'}
                </button>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* ── 2. STATS ── */}
        <ScrollReveal delay={0.1}>
          <section className="max-w-7xl mx-auto px-6 lg:px-12 -mt-8 lg:-mt-12 relative z-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
              {stats.map((stat, idx) => (
                <div key={idx} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 text-center shadow-xl border border-[#e0eeeb]">
                  <div className="text-xl lg:text-4xl font-bold bg-gradient-to-r from-[#0D3A2F] to-[#2a9d8f] bg-clip-text text-transparent mb-1 lg:mb-2">
                    {stat.value}
                  </div>
                  <div className="text-[10px] lg:text-sm text-[#4a6b63] font-bold uppercase tracking-wider">
                    {t(stat, 'label')}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </ScrollReveal>

        {/* ── 3. FEATURES (Mobil Kaydırmalı / Stack) ── */}
        <ScrollReveal delay={0.2}>
          <section id="features" className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
            <div className="text-center mb-10 lg:mb-16">
              <h2 className="text-2xl lg:text-5xl font-bold text-[#0D3A2F] mb-4">
                {lang === 'en' ? 'AI-Powered Features' : 'AI Özellikleri'}
              </h2>
            </div>

            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Feature Selection - Mobilde yan yana kaydırılabilir */}
              <div className="flex lg:flex-col gap-2 lg:gap-3 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide no-scrollbar">
                {features.map((feature, idx) => (
                  <button
                    key={feature.id}
                    onClick={() => setActiveFeature(idx)}
                    className={`flex-shrink-0 lg:w-full text-left px-4 py-3 lg:p-5 rounded-xl transition-all border ${activeFeature === idx
                        ? 'bg-[#0D3A2F] text-white border-[#0D3A2F] shadow-lg'
                        : 'bg-white text-[#0D3A2F] border-[#e0eeeb]'
                      }`}
                  >
                    <div className="flex items-center gap-3 lg:gap-4">
                      <DynamicIcon name={feature.icon} size={20} className={activeFeature === idx ? 'text-[#2a9d8f]' : 'text-[#2a9d8f]'} />
                      <span className="text-xs lg:text-base font-bold whitespace-nowrap lg:whitespace-normal">
                        {t(feature, 'title')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Feature Detail Display */}
              <div className="lg:col-span-2">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeFeature}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="bg-white rounded-2xl p-6 lg:p-10 border border-[#e0eeeb] shadow-sm h-full"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center bg-[#f0faf8]">
                        <DynamicIcon name={features[activeFeature].icon} size={30} className="text-[#2a9d8f]" />
                      </div>
                      <h3 className="text-xl lg:text-2xl font-bold text-[#0D3A2F]">{t(features[activeFeature], 'title')}</h3>
                    </div>
                    <p className="text-sm lg:text-lg text-[#4a6b63] mb-6 leading-relaxed">{t(features[activeFeature], 'description')}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                      {features[activeFeature].capabilities.map((cap, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs lg:text-sm bg-[#f5faf9] p-2 rounded-lg">
                          <span className="text-[#2a9d8f] shrink-0"><DynamicIcon name="MdCheckCircle" size={16} /></span>
                          <span className="text-[#0D3A2F] font-medium">{t(cap, 'text')}</span>
                        </div>
                      ))}
                    </div>

                    {/* Chat Example UI */}
                    <div className="bg-[#0D3A2F] rounded-xl p-4 lg:p-6 text-white overflow-hidden relative">
                      <div className="text-[10px] lg:text-xs text-[#2a9d8f] font-bold uppercase mb-4 opacity-70">
                        {t(features[activeFeature].example, 'title')}
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 bg-white/10 rounded flex items-center justify-center shrink-0">
                            <DynamicIcon name="MdPerson" size={14} />
                          </div>
                          <div className="bg-white/5 rounded-lg p-3 text-[11px] lg:text-sm max-w-[85%]">{t(features[activeFeature].example, 'input')}</div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 bg-[#2a9d8f] rounded flex items-center justify-center shrink-0">
                            <DynamicIcon name="MdAutoAwesome" size={14} />
                          </div>
                          <div className="bg-[#2a9d8f]/10 border-l-2 border-[#2a9d8f] rounded-lg p-3 text-[11px] lg:text-sm max-w-[85%]">{t(features[activeFeature].example, 'output')}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* ── 4. HOW IT WORKS ── */}
        <ScrollReveal delay={0.3}>
          <section className="bg-white py-16 lg:py-24 px-6 lg:px-12">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl lg:text-4xl font-bold text-[#0D3A2F] text-center mb-12">{lang === 'en' ? 'How It Works' : 'RUZY AI Nasıl Çalışır?'}</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {howItWorks.map((step, idx) => (
                  <div key={idx} className="relative text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#f0faf8] rounded-2xl flex items-center justify-center shadow-sm relative">
                      <DynamicIcon name={step.icon} size={32} className="text-[#2a9d8f]" />
                      <div className="absolute -top-2 -right-2 w-7 h-7 bg-[#0D3A2F] text-white rounded-full flex items-center justify-center text-xs font-bold">{step.step}</div>
                    </div>
                    <h3 className="text-base lg:text-lg font-bold mb-2">{t(step, 'title')}</h3>
                    <p className="text-xs lg:text-sm text-[#4a6b63]">{t(step, 'description')}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* ── 5. BENEFITS (Grid 2 Mobilde) ── */}
        <ScrollReveal delay={0.4}>
          <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
            <h2 className="text-2xl lg:text-4xl font-bold text-center mb-12">{lang === 'en' ? 'Benefits' : 'Avantajlar'}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="bg-white p-5 lg:p-8 rounded-2xl border border-[#e0eeeb] hover:shadow-xl transition-all text-center lg:text-left flex flex-col items-center lg:items-start">
                  <div className="w-10 h-10 lg:w-14 lg:h-14 bg-[#f0faf8] rounded-xl flex items-center justify-center mb-4 lg:mb-6">
                    <DynamicIcon name={benefit.icon} size={24} className="text-[#2a9d8f]" />
                  </div>
                  <h3 className="text-sm lg:text-lg font-bold mb-2 leading-tight">{t(benefit, 'title')}</h3>
                  <p className="hidden lg:block text-sm text-[#4a6b63]">{t(benefit, 'description')}</p>
                </div>
              ))}
            </div>
          </section>
        </ScrollReveal>

        {/* ── 6. CTA (Kompakt Dikdörtgen) ── */}
        <ScrollReveal delay={0.5}>
          <section className="px-6 py-12 lg:py-24 bg-[#f5faf9]">
            <div className="max-w-5xl mx-auto bg-[#0D3A2F] rounded-2xl lg:rounded-[3rem] p-6 lg:p-20 relative overflow-hidden text-center lg:text-left">
              <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
                <div className="lg:w-2/3">
                  <h2 className="text-2xl lg:text-5xl font-bold text-white mb-3 lg:mb-4 leading-tight">
                    {lang === 'en' ? 'Transform Your Compliance' : 'BGYS\'nizi Yapay Zeka ile Güçlendirin'}
                  </h2>
                  <p className="text-xs lg:text-lg text-white/70 max-w-xl mx-auto lg:mx-0">
                    {lang === 'en' ? 'Join organizations that save time with RUZY AI' : 'RUZY AI ile zaman kazanan organizasyonlar arasına katılın.'}
                  </p>
                </div>
                <div className="w-full lg:w-auto">
                  <button
                    onClick={() => navigate('/iletisim')}
                    className="w-full lg:w-auto bg-[#2a9d8f] text-white px-8 py-3.5 lg:py-4 rounded-xl font-bold text-sm lg:text-lg shadow-xl hover:bg-[#238a7e]"
                  >
                    {lang === 'en' ? 'Request Demo' : 'Demo Talep Et'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>
      </div>
    </>
  );
};

export default RuzyAI;